import { RefObject, useEffect, useMemo, useState } from "react";
import { styled } from "@mui/material";
import { TokenMeta } from "../../types/LandToken";
import { LngLat, metresPerPixel, staticMapSize, staticMapUrl, staticMapZoom } from "../../utils/staticMap";
import { HERO_EASE, HERO_IMAGE_MS, heroImageIn, prefersReducedMotion, reducedMotionNone } from "./heroMotion";

type Box = { width: number; height: number };

// Window resizes come in bursts; billed requests shouldn't. Re-request once the box settles.
const REQUEST_DEBOUNCE_MS = 250;

/**
 * The static-map request for a box: sized to the box's exact aspect ratio (so `cover` never
 * crops the map's edges), zoomed to the land's size (staticMapZoom), @2x on high-DPR screens,
 * re-requested only after a resize settles. Also returns the map's displayed ground scale for the
 * scale bar, and the box's width.
 */
export const useStaticMap = (
  ref: RefObject<HTMLElement>,
  lngLat: LngLat | null,
  meta: TokenMeta | null | undefined
) => {
  const [live, setLive] = useState<Box | null>(null);
  const [requested, setRequested] = useState<Box | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }
    const observer = new ResizeObserver(([entry]) => {
      const width = Math.round(entry.contentRect.width);
      const height = Math.round(entry.contentRect.height);
      if (width && height) {
        setLive((prev) => (prev && prev.width === width && prev.height === height ? prev : { width, height }));
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);

  useEffect(() => {
    if (!live) {
      return;
    }
    const same = requested && requested.width === live.width && requested.height === live.height;
    if (same) {
      return;
    }
    // First measurement goes straight out; later ones wait for the resize to settle.
    const timer = setTimeout(() => setRequested(live), requested ? REQUEST_DEBOUNCE_MS : 0);
    return () => clearTimeout(timer);
  }, [live, requested]);

  return useMemo(() => {
    if (!lngLat || !requested || !live) {
      return { url: null, metresPerCssPx: null, width: live?.width ?? null };
    }
    const size = staticMapSize(requested.width, requested.height);
    const zoom = staticMapZoom(meta, lngLat.lat, size.width, size.height);
    const url = staticMapUrl({
      ...lngLat,
      width: size.width,
      height: size.height,
      zoom,
      retina: window.devicePixelRatio > 1,
    });
    // `cover` scales the image up to the box; ground distance per CSS pixel shrinks by as much.
    const cover = Math.max(live.width / size.width, live.height / size.height);
    return { url, metresPerCssPx: metresPerPixel(lngLat.lat, zoom) / cover, width: live.width };
  }, [lngLat, meta, requested, live]);
};

type StaticMapBackgroundProps = {
  /** null: no coordinates — show the placeholder only. */
  url: string | null;
  /** Reports which url is fully on screen (null while loading, failed or absent). */
  onShownChange?: (url: string | null) => void;
};

/**
 * Two stacked images: the last fully shown map stays put underneath while the next one loads
 * and fades in over it, so a land swap cross-fades instead of flashing the placeholder. With
 * no coordinates, or if the request fails, only the placeholder shows (no broken-image icon).
 */
const StaticMapBackground = ({ url, onShownChange }: StaticMapBackgroundProps) => {
  const [base, setBase] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  const usable = url && url !== failed ? url : null;
  const incoming = usable && usable !== base ? usable : null;
  // The previous land's map must never stand in for a land without one.
  const showBase = !!usable && !!base;
  const shown = usable && usable === base ? usable : null;

  useEffect(() => {
    onShownChange?.(shown);
  }, [shown, onShownChange]);

  // Keyed by url, so promoting the incoming layer to base keeps the same <img> element — no
  // remount, no re-decode, no flash.
  const layers = [showBase ? base : null, incoming].filter((u): u is string => !!u);

  return (
    <Root aria-hidden>
      {layers.map((u) => (
        <FadeInImg key={u} src={u} onShown={() => setBase(u)} onFail={() => setFailed(u)} />
      ))}
    </Root>
  );
};

/** An image that stays invisible until loaded, then fades up to the resting 0.8. */
export const FadeInImg = ({
  src,
  onShown,
  onFail,
  className,
}: {
  src: string;
  onShown?: () => void;
  onFail?: () => void;
  className?: string;
}) => {
  const [loaded, setLoaded] = useState(false);
  return (
    <MapImg
      src={src}
      alt=""
      className={className}
      $animate={loaded}
      style={loaded ? undefined : { opacity: 0 }}
      onLoad={() => {
        setLoaded(true);
        // Reduced motion: the fade is off, so no animationend will come — it's shown now.
        if (prefersReducedMotion()) {
          onShown?.();
        }
      }}
      onAnimationEnd={(e) => e.target === e.currentTarget && onShown?.()}
      onError={() => onFail?.()}
    />
  );
};

// The design's map treatment: black under the image at 80%. The black doubles as the
// placeholder, with a faint olive wash so an absent map still reads as part of the hero.
const Root = styled("div")({
  position: "absolute",
  inset: 0,
  background: "radial-gradient(120% 90% at 30% 20%, #1f2701 0%, #000 70%)",
});

const MapImg = styled("img", {
  shouldForwardProp: (prop) => prop !== "$animate",
})<{ $animate: boolean }>(({ $animate }) => ({
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
  opacity: 0.8,
  display: "block",
  ...($animate && {
    animation: `${heroImageIn} ${HERO_IMAGE_MS}ms ${HERO_EASE} both`,
    ...reducedMotionNone,
  }),
}));

export default StaticMapBackground;
